/**
 * ThemeProvider — light/dark mode via React Context.
 *
 * Initialization precedence:
 *  1. localStorage 'ui-theme' ('light' | 'dark')
 *  2. window.matchMedia '(prefers-color-scheme: dark)'
 *  3. Default: 'light'
 *
 * Both localStorage and matchMedia access are guarded for jsdom/SSR safety.
 * Changes are persisted back to localStorage automatically.
 *
 * No Zustand, no Fluent UI imports — pure React + Web APIs.
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';

export type ThemeMode = 'light' | 'dark';

interface ThemeContextValue {
  mode: ThemeMode;
  toggleMode: () => void;
  setMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

// ---------------------------------------------------------------------------
// Initialization helpers
// ---------------------------------------------------------------------------

function readLocalStorage(): ThemeMode | null {
  try {
    const stored = localStorage.getItem('ui-theme');
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
  } catch {
    // localStorage unavailable (SSR, private-browsing restrictions, etc.)
  }
  return null;
}

function readSystemPreference(): ThemeMode {
  try {
    if (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
    ) {
      return 'dark';
    }
  } catch {
    // matchMedia unavailable (jsdom, SSR, etc.)
  }
  return 'light';
}

function getInitialMode(): ThemeMode {
  return readLocalStorage() ?? readSystemPreference();
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [mode, setModeState] = useState<ThemeMode>(getInitialMode);

  // Persist to localStorage whenever mode changes.
  useEffect(() => {
    try {
      localStorage.setItem('ui-theme', mode);
    } catch {
      // localStorage unavailable — silently ignore.
    }
  }, [mode]);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
  }, []);

  const toggleMode = useCallback(() => {
    setModeState((current) => (current === 'dark' ? 'light' : 'dark'));
  }, []);

  return (
    <ThemeContext.Provider value={{ mode, toggleMode, setMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useThemeMode(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (ctx === null) {
    throw new Error(
      'useThemeMode() must be called inside a <ThemeProvider>. ' +
        'Wrap your component tree with <ThemeProvider> before using this hook.'
    );
  }
  return ctx;
}

export default ThemeProvider;
